import Api from 'services/Api';
import _ from 'lodash';

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    RESET: 'RESET',
    UPDATE: 'UPDATE',
    TOGGLE_DISCOUNT_RATE: 'TOGGLE_DISCOUNT_RATE',
    UPDATE_PREVIEW_CLAIM: 'UPDATE_PREVIEW_CLAIM',
    UPDATE_PROGRESS: 'UPDATE_PROGRESS',
    CLEAR_PREVIEW_DATA: 'CLEAR_PREVIEW_DATA',
    CHECKING_CANCEL: 'CHECKING_CANCEL',
    CHECKING_CANCELLED: 'CHECKING_CANCELLED',
};

/**
 * Reject unselected claims and return claim ids
 *
 * @param claims
 * @return {any[]}
 */
export const selectedClaims = (claims) => {
    return _.reject(claims, function (claim) {
        return !claim.selected;
    }).map(claim => {
        return claim.id;
    });
};

function getClaimsToBeUpdatedTotal(state) {
    let selected = state.preview.data.map((data) => {
        return data.selected ? 1 : 0;
    });

    return selected.reduce((carry, total) => {
        return carry + total;
    }, 0);
}

// Initial State
const state = {
    discountRates: [
        '-2.00',
        '-1.50',
        '-1.00',
        '-0.75',
        '-0.50',
        '-0.25',
        '0.00',
        '0.50',
        '1.00',
        '1.50',
        '2.00',
        '2.50',
        '3.00'
    ],
    chosenRates: [],
    claimId: null,
    claimStatus: null,
    claimType: null,
    diseaseOrAccident: null,
    diseaseType: null,
    accidentType: null,
    gender: null,
    updateDiscountRate: null,
    discountRatesOptions: null,
    results: {},
    preview: {
        data: [],
        total: 0,
        perPage: 5,
        from: 1,
        to: 0,
        currentPage: 1,
        lastPage: 2
    },
    loading: false,
    progress: {
        numberOfClaims: 0,
        numberCompleted: 0
    },
    processing: false,
    cancelCheck: false
};

const result = (state, ...nodes) => nodes.reduce((nested, key) => !nested ? {} : nested[key], state);

// Getters
const getters = {
    individualClaim: state => state.claimStatus === 'individual',
    averageDataset: state => (rate) => [
        state['results']['average'][rate]['pastLosses'],
        state['results']['average'][rate]['futureLosses'],
    ],
    averageTotal: state => (rate) => result(state, 'results', 'average', rate, 'total'),
    averageHeadsOfLossGraph: state => (rate) => result(state, 'results', 'headsOfLoss', 'graphs', 'average')[rate],
    averageHeadsOfLossTable: state => result(state, 'results', 'headsOfLoss', 'tables', 'average'),
    totalDataset: state => (rate) => [
        state['results']['total'][rate]['pastLosses'],
        state['results']['total'][rate]['futureLosses'],
    ],
    totalTotal: state => (rate) => result(state, 'results', 'total', rate, 'total'),
    totalHeadsOfLossGraph: state => (rate) => result(state, 'results', 'headsOfLoss', 'graphs', 'total')[rate],
    totalHeadsOfLossTable: state => result(state, 'results', 'headsOfLoss', 'tables', 'total'),
    ageGroupGraph: state => (rate) => result(state, 'results', 'assessmentsByAge', 'ageGroup')[rate],
    rateGroupGraph: state => result(state, 'results', 'assessmentsByAge', 'rateGroup'),
    claimsToBeUpdatedTotal: state => getClaimsToBeUpdatedTotal(state),
};

// Actions
const actions = {
    /**
     * Commit an Update Mutation to the State.
     */
    update({commit, dispatch}, update) {
        if (update.key !== 'discountRatesOptions') {
            dispatch('clearPreview');
        }
        commit(mutationTypes.UPDATE, update);
    },
    clearPreview({commit}) {
        commit(mutationTypes.CLEAR_PREVIEW_DATA);
    },
    /**
     * Commit an Update preview claims to the State.
     */
    updateClaimSelect({state, commit}, claimId) {
        let key = _.mapKeys(state.preview.data, function (value, key) {
            if (value.id === claimId) {
                return key;
            }
        });

        commit(mutationTypes.UPDATE_PREVIEW_CLAIM, {key: _.keys(key)[0]});
    },
    /**
     * Toggle a Discount Rate in Chosen rates.
     *
     * Proceeds to reset results as well, as results will no longer be relevant.
     */
    toggleDiscountRate({commit}, rate) {
        commit(mutationTypes.TOGGLE_DISCOUNT_RATE, rate);
        commit(mutationTypes.UPDATE, {key: 'results', value: {}});
    },
    /**
     * Perform the Comparison.
     */
    compare({commit, dispatch, state}) {
        commit(mutationTypes.UPDATE, {key: 'loading', value: true});
        // Reset the results on press, it's more indicative to the user.
        dispatch('update', {key: 'results', value: {}})
            .then(() => {
                Api.dashboards.discountComparison.results({
                    chosenRates: state.chosenRates,
                    claimId: state.claimId,
                    claimStatus: state.claimStatus,
                    claimType: state.claimType,
                    diseaseOrAccident: state.diseaseOrAccident,
                    diseaseType: state.diseaseType,
                    accidentType: state.accidentType,
                    gender: state.gender,
                }).then((results) => {
                    let value = _.deeply(_.mapKeys)(results, (value, key) => {
                        // Don't camelcase the discount rates!
                        let hasNumber = /\d/;
                        if (hasNumber.test(key)) {
                            return key;
                        }

                        return _.camelCase(key);
                    });
                    commit(mutationTypes.UPDATE, {key: 'results', value});
                    commit(mutationTypes.UPDATE, {key: 'loading', value: false});
                });
            });
    },
    /**
     * Perform the preview
     */
    loadPreview({commit, dispatch, state}) {
        commit(mutationTypes.UPDATE, {key: 'loading', value: true});
        // Reset the results on press, it's more indicative to the user.

        dispatch('clearPreview')
            .then(() => {
                Api.dashboards.discountComparison.preview({
                    chosenRate: state.discountRates[state.updateDiscountRate],
                    claimStatus: state.claimStatus,
                    claimType: state.claimType,
                    diseaseOrAccident: state.diseaseOrAccident,
                    diseaseType: state.diseaseType,
                    accidentType: state.accidentType,
                    gender: state.gender,
                    page: state.preview.currentPage
                }).then((preview) => {
                    let value = _.deeply(_.mapKeys)(preview.claims, (value, key) => {
                        // Don't camelcase the discount rates!
                        if (~key.indexOf('%')) {
                            return key;
                        }

                        return _.camelCase(key);
                    });

                    commit(mutationTypes.UPDATE, {key: 'preview', value});
                    commit(mutationTypes.UPDATE, {key: 'loading', value: false});
                });
            });
    },
    /**
     * Apply the update
     */
    apply({commit, dispatch, state}) {
        commit(mutationTypes.UPDATE, {key: 'processing', value: true});
        commit(mutationTypes.UPDATE_PROGRESS, {key: 'numberOfClaims', value: 0});
        commit(mutationTypes.UPDATE_PROGRESS, {key: 'numberCompleted', value: 0});

        Api.dashboards.discountComparison.apply({
            chosenRate: state.discountRates[state.updateDiscountRate],
            applyTo: state.discountRatesOptions,
            selectedClaims: selectedClaims(state.preview.data)
        }).then(() => dispatch('checkProgress'));
    },
    /**
     * Check the progress of the discount rate update
     */
    checkProgress({commit, dispatch, state}) {
        if (state.cancelCheck) {
            commit(mutationTypes.CHECKING_CANCELLED);
            return;
        }
        Api.dashboards.discountComparison.checkProgress().then((response) => {
            if (!_.isEmpty(response.progress)) {
                commit(mutationTypes.UPDATE, {key: 'processing', value: true});
                commit(mutationTypes.UPDATE_PROGRESS, {
                    key: 'numberOfClaims',
                    value: response.progress.number_of_claims
                });
                commit(mutationTypes.UPDATE_PROGRESS, {
                    key: 'numberCompleted',
                    value: response.progress.number_completed
                });

                setTimeout(() => {
                    dispatch('checkProgress');
                }, 1000);

                return;
            }

            setTimeout(() => {
                commit(mutationTypes.UPDATE, {key: 'processing', value: false});
                dispatch('reset');
                return;
            }, 8000);
        });
    },

    cancelChecking({commit}) {
        commit(mutationTypes.CHECKING_CANCEL);
    },

    /**
     * Reset the State.
     */
    reset({commit}) {
        commit(mutationTypes.UPDATE, {key: 'loading', value: false});
        commit(mutationTypes.RESET);
    },
};

// Mutations
const mutations = {
    [mutationTypes.RESET](state) {
        state.chosenRates = [];
        state.claimId = null;
        state.claimStatus = null;
        state.claimType = null;
        state.diseaseOrAccident = null;
        state.diseaseType = null;
        state.accidentType = null;
        state.gender = null;
        state.updateDiscountRate = null;
        state.discountRatesOptions = null;
        state.results = {};
        state.preview = {
            data: [],
            total: 0,
            perPage: 5,
            from: 1,
            to: 0,
            currentPage: 1,
            lastPage: 2
        };
        state.loading = false;
        state.progress = {
            numberOfClaims: 0,
            numberCompleted: 0
        };
        state.processing = false;
    },
    [mutationTypes.UPDATE](state, {key, value}) {
        state[key] = value;
    },
    [mutationTypes.UPDATE_PROGRESS](state, {key, value}) {
        state.progress[key] = value;
    },
    [mutationTypes.CLEAR_PREVIEW_DATA](state) {
        state.preview.data = [];
    },
    [mutationTypes.TOGGLE_DISCOUNT_RATE](state, rate) {
        let index = state.chosenRates.indexOf(rate);

        index === -1
            ? state.chosenRates.push(rate)
            : state.chosenRates.splice(index, 1);
    },
    [mutationTypes.UPDATE_PREVIEW_CLAIM](state, {key}) {
        state.preview.data[key].selected = !state.preview.data[key].selected;
    },
    [mutationTypes.CHECKING_CANCEL](state) {
        state.cancelCheck = true;
    },
    [mutationTypes.CHECKING_CANCELLED](state) {
        state.cancelCheck = false;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
    modules: {}
};



// WEBPACK FOOTER //
// ./src/store/modules/discountRateComparison/index.js